import React, { useCallback, useEffect, useReducer } from "react";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import searchInsights from "search-insights";

import * as Sentry from "@sentry/browser";

import { Cart, CartItem } from "../types/cart";
import { Storefront } from "./storefront";
import { I18n } from "i18n-js";
import translations from "../../locales.json";
import { storefrontCartsPath } from "../../routes";
import { StorefrontConfig, StorefrontContext } from "../types/storefront";
import { useFetchCart, useFetchCatalog, useFetchDeals, useFetchOutOfStockList, useMutateCart } from "./queries";
import { ReactComponent as AlertCircle } from "images/icons/alert-circle.svg";
import { BottomNav } from "./bottom_nav";
import { useShowAlert } from "src/components/alert";
import {
  buildUrlForState,
  deserializeShopState,
  FocusProduct,
  NavigateCatalog,
  NavigatePage,
  NavigationContext,
  navigationReducer,
  NavigationState,
  StorefrontPage,
  UpdateSearch
} from "../types/navigation";
import { AlertProvider } from "src/components/alert";
import { CartItemEventSerializer } from "src/analytics/cart_item_event_serializer";

const globalQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: Number.POSITIVE_INFINITY,
    }
  }
});

const StorefrontLoader = ({
  config,
  catalogId,
  ageVerifiedStatus,
  queryClient
}: {
  config: StorefrontConfig;
  catalogId: string;
  ageVerifiedStatus: boolean;
  queryClient?: QueryClient;
}): React.JSX.Element => {
  return (
    <StorefrontContext.Provider value={config}>
      <QueryClientProvider client={queryClient ?? globalQueryClient}>
        <AlertProvider>
          <StorefrontLoaderInner
            config={config}
            catalogId={catalogId}
            ageVerifiedStatus={ageVerifiedStatus}
          />
        </AlertProvider>
      </QueryClientProvider>
    </StorefrontContext.Provider>
  );
}

const searchQueryFromUrl = (): string => {
  const url = new URL(window.location.toString());
  return url.searchParams.get("search_bar") || "";
}

const currentPageFromUrl = (): StorefrontPage => {
  const splits = window.location.pathname.split("/");
  const lastSegment = splits[splits.length - 1];
  switch (lastSegment) {
    case "search":
      return StorefrontPage.Search;

    case "cart":
      return StorefrontPage.Cart;

    case "categories":
      return StorefrontPage.Categories;

    default:
      return StorefrontPage.Shop;
  }
}

const StorefrontLoaderInner = ({
  config,
  catalogId,
  ageVerifiedStatus,
}: {
  config: StorefrontConfig;
  catalogId: string;
  ageVerifiedStatus: boolean;
}): React.JSX.Element => {
  const calculateInitialNavigationState = useCallback(() => {
    const currentPage = currentPageFromUrl();
    const searchQuery = searchQueryFromUrl();
    const [focusedCatalogComponent, focusedProduct] = deserializeShopState(window.location.hash.replace("#", ""));

    if (focusedProduct.length > 1) {
      // Focus the product as separate history entry so that the browser back will always close it
      const initialState = new NavigationState(currentPage, searchQuery, focusedCatalogComponent, []);
      window.history.replaceState(null, "", buildUrlForState(config, initialState));

      const initialStateWithProduct = new NavigationState(currentPage, searchQuery, focusedCatalogComponent, focusedProduct);
      window.history.pushState(null, "", buildUrlForState(config, initialStateWithProduct));

      return initialStateWithProduct;
    } else {
      return new NavigationState(currentPage, searchQuery, focusedCatalogComponent, focusedProduct);
    }
  }, [config]);

  const i18n = new I18n(translations);
  const [navigationState, updateNavigation] = useReducer(navigationReducer.bind(this, config), null, calculateInitialNavigationState);

  const catalogQuery = useFetchCatalog();
  const outOfStockQuery = useFetchOutOfStockList();
  const showAlert = useShowAlert();
  const cartQuery = useFetchCart();
  const activeDealsQuery = useFetchDeals();

  const onUrlChange = useCallback((e: Event) => {
    e.preventDefault();

    const newPage = currentPageFromUrl();
    if (navigationState.currentPage != newPage) {
      updateNavigation(NavigatePage.action(newPage, false));
    }

   const [newFocusedCatalogComponent, newFocusedProduct] = deserializeShopState(window.location.hash.replace("#", ""));
   updateNavigation(NavigateCatalog.action(newFocusedCatalogComponent, false));
   updateNavigation(FocusProduct.action(newFocusedProduct, false));
    
    const newSearchQuery = searchQueryFromUrl();
    if (newPage === StorefrontPage.Search && navigationState.searchQuery !== newSearchQuery)
      updateNavigation(UpdateSearch.action(newSearchQuery, false));
  }, [navigationState.currentPage, navigationState.searchQuery, updateNavigation]);

  useEffect(() => {
    searchInsights("init", {
      appId: config.searchAppId,
      apiKey: config.searchPublicApiKey,
      userToken: config.searchCustomerToken,
      authenticatedUserToken: config.searchAuthenticatedUserToken,
    });
  }, [config.searchAppId, config.searchAuthenticatedUserToken, config.searchCustomerToken, config.searchPublicApiKey]);

  useEffect(() => {
    window.addEventListener("popstate", onUrlChange);
    return () => {
      window.removeEventListener("popstate", onUrlChange);
    }
  }, [onUrlChange]);

  const cartMutation = useMutateCart({
    updateCartUrl: storefrontCartsPath(config.storefrontId, { catalog_id: catalogId }),
    csrfToken: config.csrfToken,
  });

  const addToCart = useCallback(async (newCartItem: CartItem, index?: number) : Promise<Cart> => {
    let newCart;
    if (!cartQuery.data) {
      newCart = [newCartItem];
    } else if (index !== undefined) {
      newCart = [...cartQuery.data.cartItems];
      newCart.splice(index, 0, newCartItem);
    } else {
      const matchingItem = [...cartQuery.data.cartItems].reverse().find(ci => ci.isStackableWith(newCartItem))

      if (matchingItem) {
        const updatedMatchingItem = matchingItem.copyWith({ quantity: newCartItem.quantity + matchingItem.quantity })

        newCart = cartQuery.data.cartItems.map(ci => ci.idTarget == updatedMatchingItem.idTarget ? updatedMatchingItem : ci);
      } else {
        newCart = cartQuery.data.cartItems.concat([newCartItem]);
      }
    }

    if (navigationState.queryId) {
      searchInsights("addedToCartObjectIDsAfterSearch", {
        index: config.searchIndexName,
        eventName: "Product added to cart after search",
        queryID: navigationState.queryId,
        objectIDs: [`${config.storefrontId};${newCartItem.key}`],
      })
    } else {
      searchInsights("addedToCartObjectIDs", {
        index: config.searchIndexName,
        eventName: "Product added to cart",
        objectIDs: [`${config.storefrontId};${newCartItem.key}`],
      })
    }

    const mutationResult = await cartMutation.mutateAsync(newCart);

    if (mutationResult.alertMessage) {
      showAlert(mutationResult.alertMessage);
    }

    if (catalogQuery.data) {
      const serializer = new CartItemEventSerializer(catalogQuery.data, config.defaultItemsEventAttributes)

      const addToCartEvent = serializer.addToCartEvent(newCartItem);

      window.dataLayer.push(addToCartEvent);
    }

    return mutationResult
  }, [cartMutation, cartQuery.data, catalogQuery.data, config.defaultItemsEventAttributes, config.searchIndexName, config.storefrontId, navigationState.queryId, showAlert]);

  const updateCart = useCallback(async (updatedCartItem: CartItem) : Promise<Cart> => {
    if (!cartQuery.data)
      throw "Can't update a cart without an existing cart";

    const previousCartItem = cartQuery.data.cartItems.find(ci => ci.idTarget == updatedCartItem.idTarget);
    const newCart = cartQuery.data.cartItems.map(ci => ci.idTarget == updatedCartItem.idTarget ? updatedCartItem : ci);

    const mutationResult = await cartMutation.mutateAsync(newCart);

    if (mutationResult.alertMessage) {
      showAlert(mutationResult.alertMessage);
    }

    if (catalogQuery.data && previousCartItem) {
      const serializer = new CartItemEventSerializer(catalogQuery.data, config.defaultItemsEventAttributes)

      if (updatedCartItem.quantity > previousCartItem.quantity) {
        const addToCartEvent = serializer.addToCartEvent(
          updatedCartItem.copyWith({ quantity: updatedCartItem.quantity - previousCartItem.quantity })
        );

        window.dataLayer.push(addToCartEvent);
      } else if (updatedCartItem.quantity < previousCartItem.quantity) {
        const removeFromCartEvent = serializer.removeFromCartEvent(
          updatedCartItem.copyWith({ quantity: previousCartItem.quantity - updatedCartItem.quantity })
        );

        window.dataLayer.push(removeFromCartEvent);
      }
    }

    return mutationResult;
  }, [cartMutation, cartQuery.data, catalogQuery.data, config.defaultItemsEventAttributes, showAlert]);

  const removeItemFromCart = useCallback(async (cartItemToRemove: CartItem) : Promise<Cart> => {
    if (!cartQuery.data)
      throw "Can't remove an item from the cart without an existing cart";

    const cartToRemoveFrom = cartQuery.data.cartItems.filter(ci => ci.name == cartItemToRemove.name);
    const restOfTheCart = cartQuery.data.cartItems.filter(ci => ci.name != cartItemToRemove.name)
    const updatedCart = cartItemToRemove.copyWith( { quantity: cartToRemoveFrom[0].quantity - 1 });
    const cart = restOfTheCart.concat(updatedCart)
    const mutationResult = await cartMutation.mutateAsync(cart);

    if (mutationResult.alertMessage) {
      showAlert(mutationResult.alertMessage);
    }

    if (catalogQuery.data) {
      const serializer = new CartItemEventSerializer(catalogQuery.data, config.defaultItemsEventAttributes)

      const removeFromCartEvent = serializer.removeFromCartEvent(cartItemToRemove);
      window.dataLayer.push(removeFromCartEvent);
    }

    return mutationResult
  }, [cartMutation, cartQuery.data, catalogQuery.data, config.defaultItemsEventAttributes, showAlert]);

  const loadingError = <>
    <div className="container space-mt-extra-desktop">
      <div className="callout has-coloured-icon has-border">
        <AlertCircle className="icon streamline-icon" />
        <div>
          <div className="title">{i18n.t("customer.storefront.loading_error.title")}</div>
          <p>{i18n.t("customer.storefront.loading_error.description")}</p>
        </div>
      </div>
    </div>
  </>;

  /*
    Everything in here replaces identical content in catalog_show.html.erb so that we
    avoid a flash of unloaded content. This is basically a super lightweight and somewhat
    fragile version of server side rendering.
  */ 
  const loadingSkeleton = <>
    <div className="divider has-content has-high-contrast-above-tablet choice-strip">
      <div className="skeleton is-chip"></div>
      <div className="skeleton is-chip space-ml-xs"></div>
    </div>

    <div className="grid has-aside" role="alert" aria-label="Loading store catalog">
      <div className="column has-container-spacing-top-above-tablet">
        <section className="has-lowered-contrast-above-tablet">
          <div className="container has-lowered-contrast">
            <div className="catalog-section-heading">
              <div className="skeleton is-heading is-small"></div>
              <div className="skeleton is-paragraph" style={{ "--lines": 1 } as React.CSSProperties}></div>
            </div>
          </div>

          <div className="container">
            <a className="product-listing">
              <div className="skeleton is-paragraph space-mt-xs space-mb-lg"></div>
            </a>

            <a className="product-listing">
              <div className="skeleton is-paragraph space-mt-xs space-mb-lg"></div>
            </a>

            <a className="product-listing">
              <div className="skeleton is-paragraph space-mt-xs space-mb-lg"></div>
            </a>
          </div>
        </section>
      </div>
    </div>
  </>;

  const inner = (() => {
    if (catalogQuery.isError || cartQuery.isError || outOfStockQuery.isError || activeDealsQuery.isError) {
      const errorMessage = catalogQuery.error || cartQuery.error || outOfStockQuery.error || activeDealsQuery.error;
      Sentry.captureMessage("Error loading catalog", { extra: { error_message: errorMessage, catalog_url: config.catalogUrl } });

      return loadingError;
    } else if (catalogQuery.isPending || cartQuery.isPending || outOfStockQuery.isLoading || activeDealsQuery.isPending) {
      return (loadingSkeleton);
    } else {

      return (
        <Storefront
          ageVerifiedStatus={ageVerifiedStatus}
          addToCart={addToCart}
          updateCart={updateCart}
          removeItemFromCart={removeItemFromCart}
          cartLoading={cartMutation.isPending}
        />
      );
    }
  })();

  return (
      <StorefrontContext.Provider value={config}>
        <NavigationContext.Provider value={[navigationState, updateNavigation]}>
          {inner}

          {config.usesNewBottomNav ? <BottomNav/> : <></>}
        </NavigationContext.Provider>
      </StorefrontContext.Provider>
  );
}

export { StorefrontLoader };
